import { useSyncExternalStore } from 'react';

/**
 * What this side has said and the server has not taken yet — a port of
 * limuzin_core's `ChatOutbox`, for the ride's chat and «Поддержка» alike.
 *
 * Tapping «Отправить» is a hand-off: the box empties in the same frame and the
 * message appears in the conversation, where it says for itself whether it has gone
 * — a clock while it is going, a tick once the server has it, and a red mark if it
 * did not go, which a tap on offers to send again or to delete. The composer never
 * holds a finished message back to watch it upload.
 *
 * Unlike the phone's, this one lives only as long as the tab: a page has no folder
 * of its own to keep a photograph in across a restart, and a queue that lost its
 * files would be a queue of messages that can never be sent.
 */
export interface OutboxMessage {
  /** Local, and the key the bubble keeps while it moves down the history. */
  id: string;
  /** «ride» or «support», which picks how it is delivered. */
  kind: 'ride' | 'support';
  /** The ride or the thread, or `NEW` for a thread the message itself will open. */
  conversation: string;
  text: string;
  files: File[];
  voice: boolean;
  /** This side's clock, until the server's copy replaces it. */
  sentAt: string;
  /** Waiting for somebody to ask for it again, rather than on its way. */
  failed: boolean;
}

/** Stands for the thread the first message will open, before it has an id. */
export const NEW = 'new';

/**
 * Delivers one message. Answers the thread a first message opened, or null. Throws
 * to say it did not go.
 */
export type Deliver = (message: OutboxMessage) => Promise<string | null>;

/** A message landed: its conversation, before and after a first message opened it. */
export interface Delivered {
  kind: OutboxMessage['kind'];
  was: string;
  conversation: string;
}

let messages: OutboxMessage[] = [];
let deliver: Deliver | null = null;
let working = false;
let counter = 0;
const changed = new Set<() => void>();
const landed = new Set<(delivery: Delivered) => void>();

function publish(next: OutboxMessage[]): void {
  messages = next;
  changed.forEach((heard) => heard());
}

/** Set by the session, which holds the API the messages go through. */
export function deliverWith(through: Deliver | null): void {
  deliver = through;
  if (through === null) publish([]);
  else void pump();
}

export function send(
  kind: OutboxMessage['kind'],
  conversation: string,
  text: string,
  files: File[] = [],
  voice = false,
): void {
  counter += 1;
  publish([
    ...messages,
    {
      id: `outbox-${Date.now()}-${counter}`,
      kind,
      conversation,
      text,
      files,
      voice,
      sentAt: new Date().toISOString(),
      failed: false,
    },
  ]);
  void pump();
}

export function retry(id: string): void {
  publish(messages.map((message) => (message.id === id ? { ...message, failed: false } : message)));
  void pump();
}

export function discard(id: string): void {
  publish(messages.filter((message) => message.id !== id));
}

/** Nothing is still on its way to a conversation that no longer exists. */
export function abandon(kind: OutboxMessage['kind'], conversation: string): void {
  publish(messages.filter((m) => !(m.kind === kind && m.conversation === conversation)));
}

export function onDelivered(heard: (delivery: Delivered) => void): () => void {
  landed.add(heard);
  return () => landed.delete(heard);
}

async function pump(): Promise<void> {
  if (working || !deliver) return;
  working = true;
  try {
    for (;;) {
      const next = messages.find((message) => !message.failed);
      if (!next || !deliver) break;
      let opened: string | null;
      try {
        opened = await deliver(next);
      } catch {
        publish(messages.map((m) => (m.id === next.id ? { ...m, failed: true } : m)));
        continue;
      }
      const conversation = opened ?? next.conversation;
      // A thread the first message opened: whatever else was said into it before it
      // had an id goes there now.
      publish(
        messages
          .filter((m) => m.id !== next.id)
          .map((m) =>
            opened && m.kind === next.kind && m.conversation === next.conversation
              ? { ...m, conversation }
              : m,
          ),
      );
      landed.forEach((heard) => heard({ kind: next.kind, was: next.conversation, conversation }));
    }
  } finally {
    working = false;
  }
}

/** Anything not taken by the server yet, gone or failed — which a reload would lose. */
export function anyPending(): boolean {
  return messages.length > 0;
}

const EMPTY: OutboxMessage[] = [];

/** What is still going out in one conversation, oldest first. */
export function usePending(kind: OutboxMessage['kind'], conversation: string | null): OutboxMessage[] {
  const all = useSyncExternalStore(
    (heard) => {
      changed.add(heard);
      return () => changed.delete(heard);
    },
    () => messages,
  );
  if (conversation === null) return EMPTY;
  return all.filter((m) => m.kind === kind && m.conversation === conversation);
}
